// src/admin/PageDetailHub.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";

/**
 * Props:
 *  - baseURL, apiKey
 * Behavior:
 *  - Tabs: overview | images | content | sections | seo
 *  - Status toggle (active/inactive) for page, subpage, images, sections (and seo_pages if you track status)
 *  - Delete for images, sections, seo_pages, content item (if id provided), and optional page/subpage
 *  - Edit modal for PUT: images (title/alt/path), sections (title/body/sort), seo (title/desc/canonical/og), content (title/subtitle/body)
 */
export default function PageDetailHub({
  baseURL = "https://gantasoft.in/rankridgeschool/dev/v1",
  apiKey = "12345678",
}) {
  const { pageId, subpageId: routeSubpageId } = useParams();
  const api = useMemo(
    () => axios.create({ baseURL, headers: { "X-API-Key": apiKey } }),
    [baseURL, apiKey]
  );

  const [page, setPage] = useState(null);
  const [subpages, setSubpages] = useState([]);
  const [activeSubpageId, setActiveSubpageId] = useState(routeSubpageId || "");
  const [tab, setTab] = useState("overview");

  // datasets
  const [images, setImages] = useState([]);
  const [sections, setSections] = useState([]);
  const [seo, setSeo] = useState([]);
  const [content, setContent] = useState(null);

  // loaders & errors
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  // edit modal
  const [editing, setEditing] = useState(null); // { entity, item }
  const [saving, setSaving] = useState(false);

  /* ------------ bootstrap: page + subpages ------------ */
  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        setErr("");
        const p = await api.get(`/pages/${pageId}`);
        setPage(p?.data?.item || null);

        const sp = await api.get(`/subpages`, {
          params: { page_id: pageId, status: "active", sort: "subpage_id", order: "asc", limit: 1000 },
        });
        setSubpages(sp?.data?.items || []);
      } catch (e) {
        setErr(e?.response?.data?.message || e.message || "Failed to load page");
      } finally {
        setLoading(false);
      }
    })();
  }, [api, pageId]);

  /* ------------ loaders by tab ------------ */
  async function loadImages() {
    try {
      const { data } = await api.get("/images", {
        params: { page_id: pageId, status: "", sort: "image_id", order: "asc", limit: 500 },
      });
      setImages(data?.items || []);
    } catch (e) { alert(e?.response?.data?.message || e.message || "Images load failed"); }
  }

  async function loadSections() {
    try {
      const { data } = await api.get("/sections", {
        params: { page_id: pageId, sort: "sort_order", order: "asc", limit: 500 },
      });
      setSections(data?.items || []);
    } catch (e) { alert(e?.response?.data?.message || e.message || "Sections load failed"); }
  }

  async function loadSeo() {
    try {
      const params = activeSubpageId ? { subpage_id: activeSubpageId } : { page_id: pageId };
      const { data } = await api.get("/seo_pages", { params });
      setSeo(data?.items || []);
    } catch (e) { alert(e?.response?.data?.message || e.message || "SEO load failed"); }
  }

  async function loadContent() {
    try {
      const params = activeSubpageId ? { subpage_id: activeSubpageId } : { page_id: pageId };
      const { data } = await api.get("/page_content", { params });
      setContent(data?.item || data?.items?.[0] || null);
    } catch (e) { alert(e?.response?.data?.message || e.message || "Content load failed"); }
  }

  useEffect(() => {
    if (!page) return;
    if (tab === "images") loadImages();
    if (tab === "sections") loadSections();
    if (tab === "seo") loadSeo();
    if (tab === "content") loadContent();
   if (tab === "faqs") loadContent();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, page, activeSubpageId]);

  /* ------------ helpers: toggle status / delete / open edit ------------ */
  const toggleStatus = async (entity, id, current) => {
    const next = current === "active" ? "inactive" : "active";
    try {
      await api.patch(`/${entity}/${id}`, { field: "status", value: next });
      // update local state
      if (entity === "pages") setPage(p => p && p.page_id === id ? { ...p, status: next } : p);
      if (entity === "subpages") setSubpages(arr => arr.map(s => s.subpage_id === id ? { ...s, status: next } : s));
      if (entity === "images") setImages(arr => arr.map(s => s.image_id === id ? { ...s, status: next } : s));
      if (entity === "sections") setSections(arr => arr.map(s => s.section_id === id ? { ...s, status: next } : s));
      if (entity === "seo_pages") setSeo(arr => arr.map(s => s.id === id ? { ...s, status: next } : s));
    } catch (e) {
      alert(e?.response?.data?.message || e.message || "Status update failed");
    }
  };

  const destroy = async (entity, id) => {
    if (!window.confirm("Delete this item? This cannot be undone.")) return;
    try {
      await api.delete(`/${entity}/${id}`);
      if (entity === "images") setImages(arr => arr.filter(x => x.image_id !== id));
      if (entity === "sections") setSections(arr => arr.filter(x => x.section_id !== id));
      if (entity === "seo_pages") setSeo(arr => arr.filter(x => x.id !== id));
      if (entity === "page_content") setContent(c => (c && c.content_id === id) ? null : c);
      if (entity === "pages") setPage(null); // optional: navigate away
      if (entity === "subpages") setSubpages(arr => arr.filter(x => x.subpage_id !== id));
    } catch (e) {
      alert(e?.response?.data?.message || e.message || "Delete failed");
    }
  };

  const openEdit = (entity, item) => setEditing({ entity, item: { ...item } });

  const saveEdit = async () => {
    if (!editing) return;
    const { entity, item } = editing;
    setSaving(true);
    try {
      // Build ID field per entity:
      const id =
        entity === "images" ? item.image_id :
        entity === "sections" ? item.section_id :
        entity === "seo_pages" ? item.id :
        entity === "page_content" ? item.content_id :
        entity === "pages" ? item.page_id :
        entity === "subpages" ? item.subpage_id : null;

      if (!id) throw new Error("Missing ID");

      await api.put(`/${entity}/${id}`, item);

      // Refresh view states quickly
      if (entity === "images") await loadImages();
      if (entity === "sections") await loadSections();
      if (entity === "seo_pages") await loadSeo();
      if (entity === "page_content") await loadContent();
      if (entity === "pages") {
        const p = await api.get(`/pages/${pageId}`);
        setPage(p?.data?.item || null);
      }
      if (entity === "subpages") {
        const sp = await api.get(`/subpages`, {
          params: { page_id: pageId, status: "active", sort: "subpage_id", order: "asc", limit: 1000 },
        });
        setSubpages(sp?.data?.items || []);
      }

      setEditing(null);
    } catch (e) {
      alert(e?.response?.data?.message || e.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="max-w-6xl mx-auto p-4 text-gray-500">Loading…</div>;
  if (err) return <div className="max-w-6xl mx-auto p-4 text-red-600">{err}</div>;

  return (
    <div className="max-w-6xl mx-auto p-4">
      {/* Header */}
      <div className="flex flex-wrap items-end gap-4 justify-between">
        <div>
          <h2 className="text-2xl font-semibold">
            {page?.page_name} <span className="text-sm text-gray-500">[ID: {page?.page_id}]</span>
          </h2>
          <div className="text-gray-600 text-sm">
            <b>Slug:</b> <code>{page?.page_slug}</code> · <b>Status:</b> {page?.status}
          </div>
        </div>

        <div className="flex gap-2">
          <button
            onClick={() => toggleStatus("pages", page.page_id, page.status)}
            className="px-3 py-1.5 text-sm rounded bg-gray-800 text-white"
          >
            {page.status === "active" ? "Deactivate" : "Activate"} Page
          </button>
          <button
            onClick={() => openEdit("pages", page)}
            className="px-3 py-1.5 text-sm rounded bg-blue-600 text-white"
          >
            Edit Page
          </button>
          <button
            onClick={() => destroy("pages", page.page_id)}
            className="px-3 py-1.5 text-sm rounded bg-red-600 text-white"
          >
            Delete Page
          </button>
        </div>
      </div>

      {/* Subpage selector */}
      <div className="mt-3 flex items-center gap-2">
        <label className="text-sm text-gray-700">Subpage:</label>
        <select
          className="border rounded px-2 py-1 text-sm"
          value={activeSubpageId}
          onChange={(e) => setActiveSubpageId(e.target.value)}
        >
          <option value="">— None (Page) —</option>
          {subpages.map(sp => (
            <option key={sp.subpage_id} value={sp.subpage_id}>
              {sp.subpage_name} (ID: {sp.subpage_id})
            </option>
          ))}
        </select>
        {activeSubpageId && (
          <>
            <button
              onClick={() => {
                const s = subpages.find(x => String(x.subpage_id) === String(activeSubpageId));
                if (s) toggleStatus("subpages", s.subpage_id, s.status);
              }}
              className="px-3 py-1.5 text-sm rounded bg-gray-800 text-white"
            >
              Toggle Subpage Status
            </button>
            <button
              onClick={() => {
                const s = subpages.find(x => String(x.subpage_id) === String(activeSubpageId));
                if (s) openEdit("subpages", s);
              }}
              className="px-3 py-1.5 text-sm rounded bg-blue-600 text-white"
            >
              Edit Subpage
            </button>
            <button
              onClick={() => destroy("subpages", Number(activeSubpageId))}
              className="px-3 py-1.5 text-sm rounded bg-red-600 text-white"
            >
              Delete Subpage
            </button>
          </>
        )}
      </div>

      {/* Tabs */}
      <div className="mt-4 border-b flex gap-2 flex-wrap">
        {["overview","images","content","sections","seo"].map(t => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-3 py-2 -mb-px border-b-2 ${tab===t ? "border-green-700 text-green-700" : "border-transparent text-gray-600 hover:text-gray-900"}`}
          >
            {t.charAt(0).toUpperCase()+t.slice(1)}
          </button>
        ))}
      </div>

      {/* Panels */}
      <div className="mt-4">
        {tab === "overview" && (
          <div className="grid gap-3">
            <div className="p-4 bg-white border rounded">
              <h3 className="font-semibold mb-2">Quick Info</h3>
              <div className="text-sm text-gray-700">
                <div><b>Created:</b> {page?.created_date}</div>
                <div><b>Updated:</b> {page?.updated_date}</div>
                <div><b>Total subpages:</b> {subpages.length}</div>
              </div>
            </div>
          </div>
        )}

        {tab === "images" && (
          <div className="p-4 bg-white border rounded">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold">Images</h3>
              <button onClick={loadImages} className="px-3 py-1.5 text-sm bg-gray-800 text-white rounded">Refresh</button>
            </div>
            {images.length === 0 ? (
              <p className="text-gray-500">No images.</p>
            ) : (
              <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3">
                {images.map(img => (
                  <div key={img.image_id} className="border rounded p-2">
                    <div className="flex items-center justify-between">
                      <div className="text-sm font-medium">{img.image_title || "(untitled)"}</div>
                      <span className="text-[11px] px-2 py-0.5 rounded-full bg-gray-100">{img.status}</span>
                    </div>
                    <div className="text-xs text-gray-600">{img.image_alt}</div>
                    <div className="aspect-video bg-gray-50 border rounded overflow-hidden mt-2">
                      <img src={"https://gantasoft.in/rankridgeschool/dev"+img.image_path} alt={img.image_alt || img.image_title} className="w-full h-full object-cover" />
                    </div>
                    <div className="flex gap-2 mt-2">
                      <button onClick={() => toggleStatus("images", img.image_id, img.status)} className="px-2 py-1 text-xs bg-gray-800 text-white rounded">
                        {img.status === "active" ? "Deactivate" : "Activate"}
                      </button>
                      <button onClick={() => openEdit("images", img)} className="px-2 py-1 text-xs bg-blue-600 text-white rounded">Edit</button>
                      <button onClick={() => destroy("images", img.image_id)} className="px-2 py-1 text-xs bg-red-600 text-white rounded">Delete</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === "content" && (
          <div className="p-4 bg-white border rounded">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold">
                Content {activeSubpageId ? `(subpage_id: ${activeSubpageId})` : `(page_id: ${pageId})`}
              </h3>
              <button onClick={loadContent} className="px-3 py-1.5 text-sm bg-gray-800 text-white rounded">Refresh</button>
            </div>
            {!content ? (
              <p className="text-gray-500">No content found.</p>
            ) : (
              <div className="grid gap-2">
                {"title" in content && <div><b>Title:</b> {content.title}</div>}
                {"subtitle" in content && <div><b>Subtitle:</b> {content.subtitle}</div>}
                {"body" in content && <div className="prose max-w-none" dangerouslySetInnerHTML={{ __html: content.body }} />}
                <div className="flex gap-2 mt-2">
                  <button onClick={() => openEdit("page_content", content)} className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded">Edit</button>
                  {"content_id" in content && (
                    <button onClick={() => destroy("page_content", content.content_id)} className="px-3 py-1.5 text-sm bg-red-600 text-white rounded">Delete</button>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {tab === "sections" && (
          <div className="p-4 bg-white border rounded">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold">Sections</h3>
              <button onClick={loadSections} className="px-3 py-1.5 text-sm bg-gray-800 text-white rounded">Refresh</button>
            </div>
            {sections.length === 0 ? (
              <p className="text-gray-500">No sections.</p>
            ) : (
              <div className="grid gap-3">
                {sections.map(sec => (
                  <div key={sec.section_id} className="border rounded p-3">
                    <div className="flex items-center justify-between">
                      <div className="font-medium">{sec.section_title || `Section #${sec.section_id}`}</div>
                      <div className="flex items-center gap-2">
                        <span className="text-[11px] px-2 py-0.5 rounded-full bg-gray-100">{sec.status}</span>
                        <span className="text-[11px] px-2 py-0.5 rounded-full bg-gray-100">Sort:{sec.sort_order ?? 0}</span>
                      </div>
                    </div>
                    {"section_body" in sec && (
                      <div className="mt-2 prose max-w-none" dangerouslySetInnerHTML={{ __html: sec.section_body }} />
                    )}
                    <div className="flex gap-2 mt-2">
                      <button onClick={() => toggleStatus("sections", sec.section_id, sec.status)} className="px-2 py-1 text-xs bg-gray-800 text-white rounded">
                        {sec.status === "active" ? "Deactivate" : "Activate"}
                      </button>
                      <button onClick={() => openEdit("sections", sec)} className="px-2 py-1 text-xs bg-blue-600 text-white rounded">Edit</button>
                      <button onClick={() => destroy("sections", sec.section_id)} className="px-2 py-1 text-xs bg-red-600 text-white rounded">Delete</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === "seo" && (
          <div className="p-4 bg-white border rounded">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold">SEO info</h3>
              <button onClick={loadSeo} className="px-3 py-1.5 text-sm bg-gray-800 text-white rounded">Refresh</button>
            </div>
            {seo.length === 0 ? (
              <p className="text-gray-500">No SEO entry.</p>
            ) : (
              <div className="grid gap-3">
                {seo.map(s => (
                  <div key={s.id} className="border rounded p-3">
                    <div className="flex items-center justify-between">
                      <div className="font-medium">{s.title}</div>
                      {"status" in s && (
                        <span className="text-[11px] px-2 py-0.5 rounded bg-gray-100">{s.status}</span>
                      )}
                    </div>
                    <div className="text-sm text-gray-700">{s.description}</div>
                    <div className="text-xs text-gray-600 mt-1">
                      <div><b>Slug:</b> <code>{s.slug}</code></div>
                      <div><b>Canonical:</b> <code>{s.canonical_path}</code></div>
                    </div>
                    {s.og_image && (
                      <div className="mt-2">
                        <img src={"https://gantasoft.in/rankridgeschool/dev"+ s.og_image} alt={s.title} className="h-28 rounded border" />
                      </div>
                    )}
                    <div className="flex gap-2 mt-2">
                      {"status" in s && (
                        <button onClick={() => toggleStatus("seo_pages", s.id, s.status)} className="px-2 py-1 text-xs bg-gray-800 text-white rounded">
                          {s.status === "active" ? "Deactivate" : "Activate"}
                        </button>
                      )}
                      <button onClick={() => openEdit("seo_pages", s)} className="px-2 py-1 text-xs bg-blue-600 text-white rounded">Edit</button>
                      <button onClick={() => destroy("seo_pages", s.id)} className="px-2 py-1 text-xs bg-red-600 text-white rounded">Delete</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Edit Modal */}
      {editing && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[85vh] overflow-y-auto" onClick={(e)=>e.stopPropagation()}>
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="font-semibold">Edit {labelFor(editing.entity)}</h3>
              <button onClick={()=>setEditing(null)} className="text-gray-500 hover:text-gray-700">✕</button>
            </div>

            <div className="p-4 grid gap-3">
              {renderEditFields(editing, setEditing)}
            </div>

            <div className="p-4 border-t flex gap-2 justify-end">
              <button onClick={saveEdit} disabled={saving} className="px-3 py-1.5 text-sm rounded bg-green-700 text-white">
                {saving ? "Saving…" : "Save"}
              </button>
              <button onClick={()=>setEditing(null)} className="px-3 py-1.5 text-sm rounded bg-gray-200">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ----- helpers for modal ----- */
function labelFor(entity) {
  switch (entity) {
    case "pages": return "Page";
    case "subpages": return "Subpage";
    case "images": return "Image";
    case "sections": return "Section";
    case "seo_pages": return "SEO";
    case "page_content": return "Content";
    default: return entity;
  }
}

function text(name, val, onChange, placeholder="") {
  return (
    <input
      className="border rounded px-3 py-2"
      name={name}
      value={val ?? ""}
      placeholder={placeholder}
      onChange={(e)=>onChange(e.target.value)}
    />
  );
}
function number(name, val, onChange) {
  return (
    <input
      type="number"
      className="border rounded px-3 py-2"
      name={name}
      value={val ?? 0}
      onChange={(e)=>onChange(Number(e.target.value))}
    />
  );
}
function textarea(name, val, onChange, rows=6) {
  return (
    <textarea
      className="border rounded px-3 py-2"
      name={name}
      rows={rows}
      value={val ?? ""}
      onChange={(e)=>onChange(e.target.value)}
    />
  );
}

function renderEditFields(editing, setEditing) {
  const { entity, item } = editing;
  const upd = (k, v) => setEditing(s => ({ ...s, item: { ...s.item, [k]: v } }));

  if (entity === "pages") {
    return (
      <>
        {text("page_name", item.page_name, v=>upd("page_name", v))}
        {text("page_slug", item.page_slug, v=>upd("page_slug", v), "/about")}
        <div className="grid grid-cols-2 gap-3">
          <label className="text-sm text-gray-700">Status</label>
          <select className="border rounded px-3 py-2" value={item.status} onChange={(e)=>upd("status", e.target.value)}>
            <option>active</option><option>inactive</option><option>deleted</option>
          </select>
        </div>
      </>
    );
  }
  if (entity === "subpages") {
    return (
      <>
        {text("subpage_name", item.subpage_name, v=>upd("subpage_name", v))}
        {text("subpage_slug", item.subpage_slug, v=>upd("subpage_slug", v), "/pre-primary")}
        <div className="grid grid-cols-2 gap-3">
          <label className="text-sm text-gray-700">Status</label>
          <select className="border rounded px-3 py-2" value={item.status} onChange={(e)=>upd("status", e.target.value)}>
            <option>active</option><option>inactive</option><option>deleted</option>
          </select>
        </div>
      </>
    );
  }
  if (entity === "images") {
    return (
      <>
        {text("image_title", item.image_title, v=>upd("image_title", v))}
        {text("image_alt", item.image_alt, v=>upd("image_alt", v))}
        {text("image_path", item.image_path, v=>upd("image_path", v), "/uploads/xyz.jpg")}
        <div className="grid grid-cols-2 gap-3">
          <label className="text-sm text-gray-700">Status</label>
          <select className="border rounded px-3 py-2" value={item.status} onChange={(e)=>upd("status", e.target.value)}>
            <option>active</option><option>inactive</option><option>deleted</option>
          </select>
        </div>
      </>
    );
  }
  if (entity === "sections") {
    return (
      <>
        {text("section_title", item.section_title, v=>upd("section_title", v))}
        {number("sort_order", item.sort_order, v=>upd("sort_order", v))}
        {textarea("section_body", item.section_body, v=>upd("section_body", v), 10)}
        <div className="grid grid-cols-2 gap-3">
          <label className="text-sm text-gray-700">Status</label>
          <select className="border rounded px-3 py-2" value={item.status} onChange={(e)=>upd("status", e.target.value)}>
            <option>active</option><option>inactive</option><option>deleted</option>
          </select>
        </div>
      </>
    );
  }
  if (entity === "seo_pages") {
    return (
      <>
        {text("slug", item.slug, v=>upd("slug", v), "/about")}
        {text("title", item.title, v=>upd("title", v))}
        {textarea("description", item.description, v=>upd("description", v), 4)}
        {text("canonical_path", item.canonical_path, v=>upd("canonical_path", v), "/about")}
        {text("og_image", item.og_image, v=>upd("og_image", v), "/uploads/og.jpg")}
        {"status" in item && (
          <div className="grid grid-cols-2 gap-3">
            <label className="text-sm text-gray-700">Status</label>
            <select className="border rounded px-3 py-2" value={item.status} onChange={(e)=>upd("status", e.target.value)}>
              <option>active</option><option>inactive</option><option>deleted</option>
            </select>
          </div>
        )}
      </>
    );
  }
  if (entity === "page_content") {
    return (
      <>
        {"title" in item && text("title", item.title, v=>upd("title", v))}
        {"subtitle" in item && text("subtitle", item.subtitle, v=>upd("subtitle", v))}
        {"body" in item && textarea("body", item.body, v=>upd("body", v), 10)}
        {"status" in item && (
          <div className="grid grid-cols-2 gap-3">
            <label className="text-sm text-gray-700">Status</label>
            <select className="border rounded px-3 py-2" value={item.status} onChange={(e)=>upd("status", e.target.value)}>
              <option>active</option><option>inactive</option><option>deleted</option>
            </select>
          </div>
        )}
      </>
    );
  }
  return <div>Unknown entity.</div>;
}
